/**
 * Cycle / tail color assignment (VIS-07) — see docs/architecture/DESIGNS-TRACK-X.md §2.
 */

import { functionalGraphCycles } from "./graph";

/** Distinct hues readable on black (sRGB CSS). */
export const CYCLE_PALETTE = [
  "#38bdf8", // sky
  "#f472b6", // pink
  "#a3e635", // lime
  "#fbbf24", // amber
  "#c084fc", // violet
  "#2dd4bf", // teal
  "#fb7185", // rose
  "#60a5fa", // blue
  "#facc15", // yellow
  "#4ade80", // green
  "#e879f9", // fuchsia
  "#94a3b8", // slate
] as const;

/** Tail fallback — slate-400, readable on black (was too close to bg when dimmed). */
const TAIL_NEUTRAL = "#94a3b8";

export type CycleColorMaps = {
  /** node → cycle index, or -1 if not on a cycle */
  cycleOf: Int32Array;
  /** node → palette index (cycle id % palette, or -1 for unresolved tail) */
  colorIndex: Int32Array;
  /** CSS color per node */
  css: string[];
  nCycles: number;
};

/**
 * Build per-node CSS colors from the friendship functional graph.
 * Tails inherit a dimmed mix of their attractor cycle’s color.
 */
export function buildCycleColors(friends: Int32Array): CycleColorMaps {
  const n = friends.length;
  const info = functionalGraphCycles(friends);
  const cycleOf = new Int32Array(n).fill(-1);
  const colorIndex = new Int32Array(n).fill(-1);
  const css: string[] = new Array(n);

  for (const [node, cid] of info.nodeToCycle) {
    cycleOf[node] = cid;
    colorIndex[node] = cid % CYCLE_PALETTE.length;
    css[node] = CYCLE_PALETTE[colorIndex[node]!]!;
  }

  // Tails: walk to cycle (bounded)
  for (let i = 0; i < n; i++) {
    if (cycleOf[i]! >= 0) continue;
    let u = i;
    let steps = 0;
    const guard = n + 2;
    let cid = -1;
    while (steps < guard) {
      const c = cycleOf[u]!;
      if (c >= 0) {
        cid = c;
        break;
      }
      u = friends[u]!;
      steps++;
    }
    if (cid >= 0) {
      colorIndex[i] = cid % CYCLE_PALETTE.length;
      // Keep ≥70% of cycle hue so tails stay visible on black (0.45 was mud).
      css[i] = dimColor(CYCLE_PALETTE[colorIndex[i]!]!, 0.72);
    } else {
      colorIndex[i] = -1;
      css[i] = TAIL_NEUTRAL;
    }
  }

  return {
    cycleOf,
    colorIndex,
    css,
    nCycles: info.cycles.length,
  };
}

/**
 * Mix color toward a lifted dark slate (not pure black) so dimmed tails
 * still clear the stage floor.
 */
export function dimColor(cssHex: string, keep: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(cssHex.trim());
  if (!m) return TAIL_NEUTRAL;
  const n = parseInt(m[1]!, 16);
  let r = (n >> 16) & 255;
  let g = (n >> 8) & 255;
  let b = n & 255;
  const k = Math.max(0, Math.min(1, keep));
  // Blend toward slate-700-ish (#334155), never pure black.
  const br = 51;
  const bg = 65;
  const bb = 85;
  r = Math.round(r * k + br * (1 - k));
  g = Math.round(g * k + bg * (1 - k));
  b = Math.round(b * k + bb * (1 - k));
  return `rgb(${r},${g},${b})`;
}
